import { $, $$, on, doc } from './util.js';

/* API REFERENCE PAGE (pages/pages/hub-api-reference.html)
   --------------------------------------------------------------------------
   Two things the template cannot do on its own:

   1. "Copy page as Markdown": the page as Markdown on the clipboard, to paste
      into an AI assistant or an issue. Built here from the rendered page, so
      the CMS stores the content once: title, lede, the module overview, then
      every endpoint with its parameters, notes, the request as HTTP and the
      first response. The button ships hidden and shows only when this runs.

   2. The right rail's "On this page" marks the section being read, as js/toc.js
      does for pages whose rail it builds itself. */

function abs(href) {
	try { return new URL(href, window.location.href).href; } catch (e) { return href; }
}

function clean(text) {
	return String(text || '').replace(/\s+/g, ' ').trim();
}

/* Inline Markdown for one element: code in backticks, links kept. */
function inline(node) {
	var out = '';
	Array.prototype.forEach.call(node.childNodes, function (child) {
		if (child.nodeType === 3) { out += child.nodeValue; return; }
		if (child.nodeType !== 1) { return; }
		var tag = child.tagName;
		if (tag === 'CODE') { out += '`' + child.textContent + '`'; }
		else if (tag === 'A' && child.getAttribute('href')) { out += '[' + clean(inline(child)) + '](' + abs(child.getAttribute('href')) + ')'; }
		else if (tag === 'STRONG' || tag === 'B') { out += '**' + clean(inline(child)) + '**'; }
		else if (tag === 'BR') { out += ' '; }
		else { out += inline(child); }
	});
	return out;
}

/* Block Markdown for a Richtext area. */
function blocks(root, md) {
	Array.prototype.forEach.call(root.children, function (el) {
		var tag = el.tagName;
		if (/^H[2-6]$/.test(tag)) { md.push('#'.repeat(Math.min(6, Number(tag[1]) + 1)) + ' ' + clean(el.textContent), ''); }
		else if (tag === 'P') { var t = clean(inline(el)); if (t) { md.push(t, ''); } }
		else if (tag === 'UL' || tag === 'OL') {
			$$(':scope > li', el).forEach(function (li, i) { md.push((tag === 'OL' ? (i + 1) + '. ' : '- ') + clean(inline(li))); });
			md.push('');
		}
		else if (tag === 'PRE') { md.push('```', el.textContent.replace(/\s+$/, ''), '```', ''); }
		else if (tag === 'BLOCKQUOTE') { md.push('> ' + clean(inline(el)), ''); }
		else if (tag === 'TABLE') {
			$$('tr', el).forEach(function (tr) { md.push('| ' + $$('th, td', tr).map(function (c) { return clean(inline(c)); }).join(' | ') + ' |'); });
			md.push('');
		}
		else if (el.children.length) { blocks(el, md); }
	});
}

/* One parameter or key field: `name` (in, type, required): description. */
function param(row) {
	var meta = $$('.api-in, .p-type, .api-req', row).map(function (s) { return clean(s.textContent); }).filter(Boolean).join(', ');
	var desc = $('.p-desc', row);
	return '- `' + clean($('.p-name', row).textContent) + '`' + (meta ? ' (' + meta + ')' : '') + (desc ? ': ' + clean(inline(desc)) : '');
}

function pills(root, md) {
	var links = $$('.xref', root);
	if (!links.length) { return; }
	md.push('Learn more:');
	links.forEach(function (a) {
		var kind = clean($('.xref-k', a) && $('.xref-k', a).textContent);
		var title = clean($('.xref-t', a) && $('.xref-t', a).textContent);
		md.push('- ' + kind + ': ' + (a.getAttribute('href') ? '[' + title + '](' + abs(a.getAttribute('href')) + ')' : title));
	});
	md.push('');
}

function example(ex, md) {
	var http = $('.api-panel[data-lang="http"] code', ex);
	if (http) { md.push('```http', http.textContent.replace(/\s+$/, ''), '```', ''); }
	var responses = $$('.api-res', ex);
	var statuses = $$('.api-res-head .api-status', ex);
	responses.forEach(function (res, i) {
		var el = statuses[i];
		var label = el && el.nextElementSibling && el.nextElementSibling.classList.contains('api-res-label') ? el.nextElementSibling.textContent : '';
		var status = el ? clean((el.getAttribute('data-status') || el.textContent) + (label ? ', ' + label : '')) : '';
		md.push('Response' + (status ? ' ' + status : '') + ':', '', '```json', $('code', res).textContent.replace(/\s+$/, ''), '```', '');
	});
}

function pageMarkdown(page) {
	var md = [];
	var head = $('.api-head', page);
	md.push('# ' + clean($('.ref-h1', head).textContent), '');
	var lede = $('.ref-lede', head);
	if (lede) { md.push(clean(inline(lede)), ''); }
	var prefix = $('.api-prefix code', head);
	if (prefix) { md.push('Prefix: `' + clean(prefix.textContent) + '`', ''); }
	md.push('Source: ' + window.location.href.split('#')[0], '');

	var about = $('.api-about', page);
	if (about) {
		md.push('## ' + clean($('h2', about).textContent), '');
		var intro = $('.api-about-intro', about);
		if (intro) { blocks(intro, md); }
		var traits = $$('.api-trait', about);
		traits.forEach(function (t) {
			var badge = $('.api-yes, .api-no', t);
			var text = $('p', t);
			md.push('- **' + clean($('.api-trait-name', t).textContent) + '**' + (badge ? ': ' + clean(badge.textContent) + '.' : '') + (text ? ' ' + clean(inline(text)) : ''));
		});
		if (traits.length) { md.push(''); }
		var fields = $$('.api-fields .api-param', about);
		if (fields.length) {
			md.push('Key fields:');
			fields.forEach(function (row) { md.push(param(row)); });
			md.push('');
		}
		pills($('.api-learn', about) || doc.createElement('div'), md);
	}

	$$('.api-body', page).forEach(function (body) { blocks(body, md); });

	$$('.api-ep', page).forEach(function (ep) {
		md.push('## ' + clean($('.api-ep-title > span', ep).textContent), '');
		md.push('`' + clean($('.api-m', ep).textContent) + ' ' + clean($('.api-path', ep).textContent) + '`', '');
		var desc = $('.api-desc', ep);
		if (desc) { blocks(desc, md); }
		$$('.api-facts dt', ep).forEach(function (dt) {
			md.push('- ' + clean(dt.textContent) + ': ' + clean(inline(dt.nextElementSibling)));
		});
		md.push('');
		var rows = $$('.api-params .api-param', ep);
		if (rows.length) {
			md.push('Parameters:');
			rows.forEach(function (row) { md.push(param(row)); });
			md.push('');
		}
		$$('.api-notes .callout-body', ep).forEach(function (note) { md.push('> ' + clean(inline(note)), ''); });
		var ex = $('.api-ex', ep);
		if (ex) { example(ex, md); }
		pills($('.api-learn', ep) || doc.createElement('div'), md);
	});

	return md.join('\n').replace(/\n{3,}/g, '\n\n').trim() + '\n';
}

function initCopyMarkdown(page) {
	var button = $('#apiCopyMd', page);
	if (!button || !navigator.clipboard || !navigator.clipboard.writeText) { return; }
	var label = button.textContent;
	button.hidden = false;
	on(button, 'click', function () {
		var text;
		try { text = pageMarkdown(page); } catch (e) { return; }
		navigator.clipboard.writeText(text).then(function () {
			button.textContent = 'Copied as Markdown';
			setTimeout(function () { button.textContent = label; }, 1600);
		}, function () {});
	});
}

function initSpy(page) {
	var links = $$('.api-toc > a.toc-item', page).map(function (a) {
		var id = (a.getAttribute('href') || '').slice(1);
		return { a: a, el: id ? doc.getElementById(id) : null };
	}).filter(function (l) { return l.el; });
	if (!links.length) { return; }

	function spy() {
		var top = window.pageYOffset + 120;
		var current = links[0];
		links.forEach(function (l) { if (l.el.getBoundingClientRect().top + window.pageYOffset <= top) { current = l; } });
		links.forEach(function (l) { l.a.classList.toggle('active', l === current); });
	}
	var ticking = false;
	on(window, 'scroll', function () {
		if (ticking) { return; }
		ticking = true;
		window.requestAnimationFrame(function () { spy(); ticking = false; });
	});
	spy();
}

export function initApiPage() {
	var page = $('[data-api-page]');
	if (!page) { return; }
	initCopyMarkdown(page);
	initSpy(page);
}
